import React, { useEffect, useRef, useState } from 'react'
import { Route, Switch } from 'react-router-dom'
import { t } from '@lingui/macro'

import { CustomTranslationsTab } from './custom-translations-tab/CustomTranslationsTab'

import { staticLinks } from 'shared/linkBuilder'
import HeadTitle from '../../components/common/head/HeadTitle'
import PageHeader from '../../components/common/page/header/PageHeader'
import { Page } from '../../components/common/page/Page'
import { apolloClientPrivate } from '../../graphql/apolloClient'
import { AccountCampaignsTab } from './AccountCampaignsTab'
import {
  OrganisationDetailFragment,
  useOrganisationDetailQuery,
  useOrganisationPatchMutation
} from './AccountPage.codegen'
import { AuthenticationTab } from './authentication-tab/AuthenticationTab'
import CustomiseTab from './customise-tab/CustomiseTab'
import NotificationsTab from './notifications-tab/NotificationsTab'
import { uploadLogoToS3 } from './uploadLogo'
import { ProvisioningTab } from './provisioning-tab/ProvisioningTab'

import {
  accountPageDefaultFromState,
  createAccountPageForm,
  dirtyVariableProps,
  gqlInputVariableFromForms
} from './accountPageUtils'
import {
  AccountPageForm,
  AccountPageForms,
  AccountPageFormTypes,
  AccountPageState
} from './accountPageTypes'
import { PageWithTabs } from '../../components/common/tabs/NavLinkTabs'
import AccountPageTabs from './AccountPageTabs'

import './AccountPage.less'
import { useFeatureFlags } from '../../app/hooks/useFeatureFlags'

export const AccountPage = () => {
  const { featureFlags } = useFeatureFlags()
  const { data: organisationData } = useOrganisationDetailQuery()
  const [patchOrganisation, { loading: isLoading }] =
    useOrganisationPatchMutation()
  const isFormsInitialized = useRef(false)
  const [state, setState] = useState<AccountPageState>({
    forms: { ...accountPageDefaultFromState }
  })

  const renderTab = (TabComponent, pageForm: string) => {
    return (
      <TabComponent
        loading={isLoading}
        form={pageForm && state.forms[pageForm]}
        onFormChange={(form: AccountPageForm) =>
          handleFormChange({ [pageForm]: form })
        }
      />
    )
  }

  const createFormsFromOrganisation = (
    organisation: OrganisationDetailFragment
  ) => {
    const forms = createAccountPageForm(organisation)
    setState({ ...state, forms })
  }

  const handleFormChange = async (formState: AccountPageForm) => {
    const forms = {
      ...state.forms,
      ...formState
    }

    await sendPatchRequests(forms)
  }

  const sendPatchRequests = async (newForms: AccountPageForms) => {
    const { forms } = state

    const formValues = gqlInputVariableFromForms(newForms)
    const pristineFormValues = gqlInputVariableFromForms(forms)
    const gqlVars = dirtyVariableProps(pristineFormValues, formValues)

    if (gqlVars.styles?.logo) {
      const logoUrl = await uploadLogoToS3(
        apolloClientPrivate,
        gqlVars.styles.logo
      )
      gqlVars.styles.logo = {
        original: logoUrl,
        variants: []
      }
    }

    if (!Object.keys(gqlVars).length) return

    const { data: result } = await patchOrganisation({
      variables: { input: gqlVars }
    })
    const organisation = result?.admin?.organisation?.patch

    createFormsFromOrganisation(organisation!)
  }

  useEffect(() => {
    // We don't need to initialize form at every update
    if (!isFormsInitialized.current && organisationData?.admin?.organisation) {
      isFormsInitialized.current = true
      createFormsFromOrganisation(organisationData.admin.organisation)
    }
  }, [organisationData])

  return (
    <Page className="AccountPage">
      <HeadTitle title={t`Account settings`} />
      <PageHeader title={t`Account settings`} className="PageHeader" />
      <PageWithTabs
        tabs={<AccountPageTabs />}
        panels={
          <Switch>
            <Route
              exact
              path={staticLinks.accountSettings}
              render={() => {
                const { forms } = state
                const form = forms[AccountPageFormTypes.Customise]
                const props = {
                  loading: isLoading,
                  form
                }
                return (
                  <CustomiseTab
                    onFormChange={async (form) => {
                      await handleFormChange({
                        [AccountPageFormTypes.Customise]: form
                      } as AccountPageForms)
                    }}
                    {...props}
                  />
                )
              }}
            />
            <Route
              exact
              path={staticLinks.accountAuthenticationOptions}
              render={() =>
                renderTab(
                  AuthenticationTab,
                  AccountPageFormTypes.Authentication
                )
              }
            />
            <Route
              exact
              path={staticLinks.accountNotifications}
              render={() =>
                renderTab(NotificationsTab, AccountPageFormTypes.Notifications)
              }
            />
            <Route
              exact
              path={staticLinks.accountProvisioning}
              render={() => renderTab(ProvisioningTab, 'provisioning')}
            />
            {featureFlags?.custom_i18n && (
              <Route
                exact
                path={staticLinks.accountCustomTranslations}
                render={() =>
                  renderTab(CustomTranslationsTab, 'custom_translations')
                }
              />
            )}
            {featureFlags?.campaigns && (
              <Route
                exact
                path={staticLinks.accountCampaigns}
                render={() => renderTab(AccountCampaignsTab, 'campaigns')}
              />
            )}
          </Switch>
        }
      />
    </Page>
  )
}
